import { initDB } from "./indexedDB";
import type { IDBPDatabase } from 'idb';

// Store names
const SUBSIDY_ALLOCATION_STORE = "subsidy_allocations";
const BENEFICIARIES_STORE = "subsidy_beneficiaries";
const SUBSIDY_USAGE_STORE = "subsidy_usage";
const SUBSIDY_SETTINGS_STORE = "subsidy_settings";

// Initialize the database
export const initializeSubsidyDatabase = async (): Promise<void> => {
  try {
    console.log("Initializing subsidy database stores...");
    await initDB();
    console.log("Database initialized successfully");
    return Promise.resolve();
  } catch (error) {
    console.error("Failed to initialize subsidy database:", error);
    return Promise.reject(error);
  }
};

// Types
export interface SubsidyAllocation {
  allocationId: string;
  customerName: string;
  customerId: string;
  icNumber: string;
  memberType: "Customer" | "Coop Member";
  incomeRange: string;
  subsidyAmount: number;
  subsidyType: "Monthly" | "One-Time" | "Conditional";
  eligibleCategories: string[];
  allocationDate: Date;
  expiryDate: Date;
  assignedBy: string;
  notes: string;
  status: "Active" | "Expired" | "Used" | "Pending";
  needsSync?: boolean;
  lastModified?: number;
}

export interface SubsidyBeneficiary {
  beneficiaryId: string;
  name: string;
  icNumber: string;
  phone: string;
  memberType: "Customer" | "Coop Member";
  incomeBracket: string;
  tags: string[];
  lastSubsidyReceived?: Date;
  totalSubsidyUsed: number;
  avgSpendPerVisit: number;
  location: string;
  needsSync?: boolean;
  lastModified?: number;
}

export interface SubsidyUsage {
  usageId: string;
  beneficiaryId: string;
  beneficiaryName: string;
  productId: string;
  productName: string;
  productCategory: string;
  subsidyAmount: number;
  usageDate: Date;
  outletName: string;
  posTerminal: string;
  cashier: string;
  needsSync?: boolean;
  lastModified?: number;
}

export interface SubsidySettings {
  id: string;
  eligibleProductCategories: {
    categoryId: string;
    categoryName: string;
    skuMapping: string[];
  }[];
  maxSubsidyPerPerson: number;
  maxSubsidyPerMonth: number;
  minSpendRequired: number;
  autoTaggingRules: {
    tag: string;
    condition: string;
    value: any;
  }[];
  expiryRules: {
    daysValid: number;
    expireAtEndOfMonth: boolean;
  };
  notificationSettings: {
    enableEmail: boolean;
    enableSMS: boolean;
    enableAppNotifications: boolean;
  };
  needsSync?: boolean;
  lastModified?: number;
}

// CRUD operations for Subsidy Allocations
export const saveSubsidyAllocation = async (allocation: SubsidyAllocation): Promise<string> => {
  try {
    const db = await initDB();
    const tx = db.transaction(SUBSIDY_ALLOCATION_STORE, 'readwrite');
    const store = tx.objectStore(SUBSIDY_ALLOCATION_STORE);
    
    const allocationWithMeta = {
      ...allocation,
      lastModified: new Date().getTime(),
      needsSync: true
    };
    
    await store.put(allocationWithMeta);
    await tx.done;
    console.log("Subsidy allocation saved successfully");
    return allocation.allocationId;
  } catch (error) {
    console.error("Error saving subsidy allocation:", error);
    throw new Error("Failed to save subsidy allocation");
  }
};

export const getAllSubsidyAllocations = async (): Promise<SubsidyAllocation[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(SUBSIDY_ALLOCATION_STORE, 'readonly');
    const store = tx.objectStore(SUBSIDY_ALLOCATION_STORE);
    const allocations = await store.getAll();
    await tx.done;
    return allocations || [];
  } catch (error) {
    console.error("Error getting subsidy allocations:", error);
    throw new Error("Failed to get subsidy allocations");
  }
};

// CRUD operations for Beneficiaries
export const saveBeneficiary = async (beneficiary: SubsidyBeneficiary): Promise<string> => {
  try {
    const db = await initDB();
    const tx = db.transaction(BENEFICIARIES_STORE, 'readwrite');
    const store = tx.objectStore(BENEFICIARIES_STORE);
    
    const beneficiaryWithMeta = {
      ...beneficiary,
      lastModified: new Date().getTime(),
      needsSync: true
    };
    
    await store.put(beneficiaryWithMeta);
    await tx.done;
    console.log("Beneficiary saved successfully");
    return beneficiary.beneficiaryId;
  } catch (error) {
    console.error("Error saving beneficiary:", error);
    throw new Error("Failed to save beneficiary");
  }
};

export const getAllBeneficiaries = async (): Promise<SubsidyBeneficiary[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(BENEFICIARIES_STORE, 'readonly');
    const store = tx.objectStore(BENEFICIARIES_STORE);
    const beneficiaries = await store.getAll();
    await tx.done;
    return beneficiaries || [];
  } catch (error) {
    console.error("Error getting beneficiaries:", error);
    throw new Error("Failed to get beneficiaries");
  }
};

// CRUD operations for Subsidy Usage
export const saveSubsidyUsage = async (usage: SubsidyUsage): Promise<string> => {
  try {
    const db = await initDB();
    const tx = db.transaction(SUBSIDY_USAGE_STORE, 'readwrite');
    const store = tx.objectStore(SUBSIDY_USAGE_STORE);
    
    const usageWithMeta = {
      ...usage,
      lastModified: new Date().getTime(),
      needsSync: true
    };
    
    await store.put(usageWithMeta);
    await tx.done;
    console.log("Subsidy usage saved successfully");
    return usage.usageId;
  } catch (error) {
    console.error("Error saving subsidy usage:", error);
    throw new Error("Failed to save subsidy usage");
  }
};

export const getAllSubsidyUsages = async (): Promise<SubsidyUsage[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(SUBSIDY_USAGE_STORE, 'readonly');
    const store = tx.objectStore(SUBSIDY_USAGE_STORE);
    const usages = await store.getAll();
    await tx.done;
    return usages || [];
  } catch (error) {
    console.error("Error getting subsidy usages:", error);
    throw new Error("Failed to get subsidy usages");
  }
};

// CRUD operations for Subsidy Settings
export const saveSubsidySettings = async (settings: SubsidySettings): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(SUBSIDY_SETTINGS_STORE, 'readwrite');
    const store = tx.objectStore(SUBSIDY_SETTINGS_STORE);
    
    const settingsWithMeta = {
      ...settings,
      lastModified: new Date().getTime(),
      needsSync: true
    };
    
    await store.put(settingsWithMeta);
    await tx.done;
    console.log("Subsidy settings saved successfully");
  } catch (error) {
    console.error("Error saving subsidy settings:", error);
    throw new Error("Failed to save subsidy settings");
  }
};

export const getSubsidySettings = async (): Promise<SubsidySettings | null> => {
  try {
    const db = await initDB();
    const tx = db.transaction(SUBSIDY_SETTINGS_STORE, 'readonly');
    const store = tx.objectStore(SUBSIDY_SETTINGS_STORE);
    const settings = await store.get("subsidy_settings");
    await tx.done;
    return settings || null;
  } catch (error) {
    console.error("Error getting subsidy settings:", error);
    throw new Error("Failed to get subsidy settings");
  }
};

// Initialize with default settings
export const initializeSubsidySettings = async (): Promise<void> => {
  try {
    const existingSettings = await getSubsidySettings();
    
    if (!existingSettings) {
      const defaultSettings: SubsidySettings = {
        id: "subsidy_settings",
        eligibleProductCategories: [
          { categoryId: "cat_food", categoryName: "Food Essentials", skuMapping: [] },
          { categoryId: "cat_education", categoryName: "Education", skuMapping: [] }
        ],
        maxSubsidyPerPerson: 200,
        maxSubsidyPerMonth: 100,
        minSpendRequired: 10,
        autoTaggingRules: [
          { tag: "B40", condition: "income_below", value: 3000 }
        ],
        expiryRules: {
          daysValid: 30,
          expireAtEndOfMonth: true
        },
        notificationSettings: {
          enableEmail: true,
          enableSMS: true,
          enableAppNotifications: false
        }
      };
      
      await saveSubsidySettings(defaultSettings);
      console.log("Initialized default subsidy settings");
    }
  } catch (error) {
    console.error("Error initializing subsidy settings:", error);
    throw error;
  }
};

// Generate sample data for testing
export const generateSampleSubsidyData = async (): Promise<void> => {
  try {
    // Generate sample allocations
    const sampleAllocations: SubsidyAllocation[] = [
      {
        allocationId: "sa_001",
        customerName: "Ahmad bin Ismail",
        customerId: "cust_001",
        icNumber: "881212-10-4321",
        memberType: "Customer",
        incomeRange: "RM2000-RM3000",
        subsidyAmount: 150,
        subsidyType: "Monthly",
        eligibleCategories: ["Food Essentials", "Education"],
        allocationDate: new Date(2024, 4, 1),
        expiryDate: new Date(2024, 4, 30),
        assignedBy: "System Admin",
        notes: "Low-income family with 3 school children",
        status: "Active"
      },
      {
        allocationId: "sa_002",
        customerName: "Nur Farah binti Abdullah",
        customerId: "cust_002",
        icNumber: "921010-11-5678",
        memberType: "Coop Member",
        incomeRange: "RM1000-RM2000",
        subsidyAmount: 200,
        subsidyType: "Monthly",
        eligibleCategories: ["Food Essentials"],
        allocationDate: new Date(2024, 4, 5),
        expiryDate: new Date(2024, 4, 30),
        assignedBy: "System Admin",
        notes: "Single parent with 2 children",
        status: "Active"
      }
    ];
    
    for (const allocation of sampleAllocations) {
      await saveSubsidyAllocation(allocation);
    }
    
    // Generate sample beneficiaries
    const sampleBeneficiaries: SubsidyBeneficiary[] = [
      {
        beneficiaryId: "ben_001",
        name: "Ahmad bin Ismail",
        icNumber: "881212-10-4321",
        phone: "0123456789",
        memberType: "Customer",
        incomeBracket: "RM2000-RM3000",
        tags: ["B40", "Family"],
        lastSubsidyReceived: new Date(2024, 4, 1),
        totalSubsidyUsed: 85.50,
        avgSpendPerVisit: 35.20,
        location: "Kuala Lumpur"
      },
      {
        beneficiaryId: "ben_002",
        name: "Nur Farah binti Abdullah",
        icNumber: "921010-11-5678",
        phone: "0198765432",
        memberType: "Coop Member",
        incomeBracket: "RM1000-RM2000",
        tags: ["B40", "Single Parent"],
        lastSubsidyReceived: new Date(2024, 4, 5),
        totalSubsidyUsed: 120.75,
        avgSpendPerVisit: 40.25,
        location: "Petaling Jaya"
      }
    ];
    
    for (const beneficiary of sampleBeneficiaries) {
      await saveBeneficiary(beneficiary);
    }
    
    // Generate sample usage records
    const sampleUsages: SubsidyUsage[] = [
      {
        usageId: "usage_001",
        beneficiaryId: "ben_001",
        beneficiaryName: "Ahmad bin Ismail",
        productId: "prod_001",
        productName: "Rice 5kg",
        productCategory: "Food Essentials",
        subsidyAmount: 15.50,
        usageDate: new Date(2024, 4, 10),
        outletName: "Main Store",
        posTerminal: "POS-001",
        cashier: "Ali"
      },
      {
        usageId: "usage_002",
        beneficiaryId: "ben_001",
        beneficiaryName: "Ahmad bin Ismail",
        productId: "prod_002",
        productName: "School Textbooks",
        productCategory: "Education",
        subsidyAmount: 30.00,
        usageDate: new Date(2024, 4, 15),
        outletName: "Main Store",
        posTerminal: "POS-002",
        cashier: "Muthu"
      }
    ];
    
    for (const usage of sampleUsages) {
      await saveSubsidyUsage(usage);
    }
    
    // Initialize settings
    await initializeSubsidySettings();
    
    console.log("Sample subsidy data generated successfully");
  } catch (error) {
    console.error("Error generating sample subsidy data:", error);
    throw error;
  }
};
